import { useMutation, useQueryClient } from 'react-query'
import { useAxiosContext } from '@dc-shared-web/providers/AxiosProvider'
import { useToast } from '@dc-shared-web/components/shadcn/use-toast'
import { CrunchData } from '@dc-shared-web/hooks/api/document/types'
import {
  DOCUMENTS_QUERY_KEY,
  LIST_QUERY_KEY,
} from '@dc-shared-web/hooks/api/queryKeys'
import {
  CrunchesPageEntities,
  shouldInvalidateCrunchesPage,
} from '@dc-shared-web/hooks/utils/shouldInvalidateCrunchesPage'
import { renameDocument } from '@dc-shared-web/hooks/api/document/renameDocument'
import type { RenameDocumentParams } from '@dc-shared-web/hooks/api/document/renameDocument'

export const useRenameDocument = (params: {
  documentId: string
  onSuccess?: () => void
}) => {
  const apiClient = useAxiosContext()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation(
    (mutationParams: RenameDocumentParams) =>
      renameDocument(apiClient, mutationParams),
    {
      onSuccess: async (_, successParams) => {
        await queryClient.invalidateQueries<CrunchesPageEntities>({
          queryKey: [DOCUMENTS_QUERY_KEY, LIST_QUERY_KEY],
          refetchPage: (page) =>
            shouldInvalidateCrunchesPage(page, params.documentId),
        })

        await queryClient.invalidateQueries<CrunchData[]>({
          queryKey: ['Document', params.documentId],
        })

        toast({
          variant: 'success',
          title: 'Document renamed',
          description: `${successParams.oldName} renamed to ${successParams.label}.`,
        })
        params?.onSuccess?.()
      },
      onError: () => {
        toast({
          variant: 'error',
          title: 'Error renaming document',
          description: 'If the issue persists, please contact support.',
        })
      },
    }
  )
}
